import React, { useContext, useEffect, useState } from 'react';
import * as Styled from './styled';
import { MarketOrder } from './components/MarketOrder';
import { LimitOrder } from './components/LimitOrder';
import { TradingContext } from '@trader/contexts';
import {
  IInstrumentDetailsBE,
  IOrderDetailsBE,
  TPlaceOrderType,
} from '@trader/api';
import { SuccessChangeOrder } from '../components/successChangeOrder';
import { TInstrumentEntity, TModalStore, useMst } from '@trader/store';
import { MODAL_TYPES } from '@trader/constants';
import { useI18next } from '@trader/services';
import { observer } from 'mobx-react-lite';
import {
  ModalTitle,
  Progress,
  Tabs,
  TradingIsClosed,
} from '@trader/components';
import { useBusinessLogic } from '@trader/hooks';

export const CreateOrder: React.FC = observer(() => {
  const store = useMst();
  const placedOrder = store.pages.trading.placedOrder;
  const { clearPreviousState, handleStartTrading, instrument } =
    useContext(TradingContext);
  const { applyCondition } = useBusinessLogic('first_case');
  const { applyCondition: applyConditionThird } =
    useBusinessLogic('third_case');
  const { translate } = useI18next();

  const [activeTab, setActiveTab] = useState<TPlaceOrderType>('Market');

  const modal: TModalStore = store.ui.modal;
  const symbol = modal.options.get('symbol');
  const buttonType = modal.options.get('type');
  const freeMargin = store.user.portfolio.freeMargin;

  const getInstrumentDetails = async () => {
    if (symbol) {
      const response = (await store.entities.instruments
        .get<TInstrumentEntity>(symbol)
        ?.getAllInstrumentDetailsAsync.run(symbol)) as [
        IInstrumentDetailsBE,
        IOrderDetailsBE
      ];

      if (response) {
        const [detailsResult, orderDetailsResult] = response;
        handleStartTrading(
          {
            ...detailsResult,
            ...orderDetailsResult,
          },
          buttonType
        );
      }
    }
  };

  const addDeposit = () => {
    store.ui.modal.open(MODAL_TYPES.addFunds);
  };

  const changeTab = (value: string) => () => {
    setActiveTab(value as TPlaceOrderType);
  };

  useEffect(() => {
    getInstrumentDetails();
    return () => {
      clearPreviousState();
      store.entities.ordersMetrics.getOrdersMetricsAsync.run();
      store.entities.positionsMetrics.getPositionsMetricsAsync.run();
    };
  }, [symbol]);

  useEffect(() => {
    return () => {
      clearPreviousState();
      setActiveTab('Market');
    };
  }, []);

  if (!instrument.general.symbol) {
    return (
      <Styled.Root $isLoading>
        <Progress />
      </Styled.Root>
    );
  }

  if (applyCondition().status === 'failed') {
    return (
      <Styled.Root height={300}>
        <TradingIsClosed
          iconTypeName='completeRegistration'
          title={translate('WARNINGS.COMPLETE_REGISTRATION')}
          content={translate('WARNINGS.HAVENT_COMPLETED_REGISTRATION')}
          buttonContent={translate('COMMON.LABELS.PROCEED')}
          onClick={applyCondition()?.action}
        />
      </Styled.Root>
    );
  }

  if (applyConditionThird().status === 'failed') {
    return (
      <Styled.Root $isLoading height={300}>
        <TradingIsClosed
          iconTypeName='warning'
          title={translate('WARNINGS.TRADING_IS_DISABLED')}
          content={translate('WARNINGS.PLEASE_CONTACT_SUPPORT', {
            email: import.meta.env.VITE_SUPPORT_EMAIL,
          })}
          buttonContent={translate('COMMON.LABELS.PROCEED')}
          onClick={applyConditionThird()?.action}
        />
      </Styled.Root>
    );
  }

  if (instrument.general.tradingStatus === 'CLOSE_ONLY') {
    return (
      <Styled.Root>
        <TradingIsClosed
          iconTypeName='warning'
          title={translate('WARNINGS.TRADING_LIMITED_TO_CLOSE_ONLY_POSITIONS')}
        />
      </Styled.Root>
    );
  }

  const tabs = [
    {
      value: 'Market',
      label: translate('COMMON.LABELS.MARKET'),
      children: <MarketOrder addDeposit={addDeposit} freeMargin={freeMargin} />,
    },
    {
      value: 'EntryLimit',
      label: translate('COMMON.LABELS.LIMIT_STOP_ORDER'),
      children: <LimitOrder freeMargin={freeMargin} />,
    },
  ];

  return (
    <Styled.Root>
      {!placedOrder ? (
        <React.Fragment>
          <ModalTitle
            title={symbol}
            bottomMargin={import.meta.env.VITE_IS_DMA && '0'}
          />
          {import.meta.env.VITE_IS_DMA ? (
            <MarketOrder addDeposit={addDeposit} freeMargin={freeMargin} />
          ) : (
            <Tabs tabsPosition='top' tabs={tabs} onChange={changeTab} />
          )}
        </React.Fragment>
      ) : (
        <SuccessChangeOrder
          type={activeTab === 'Market' ? 'market' : 'limit'}
        />
      )}
    </Styled.Root>
  );
});
